<template>
  <div class="mx-auto px-4 py-4 w-[90%]">
    <!-- Шапка -->
    <div class="flex items-center justify-between mb-4">
      <div>
        <button type="button" @click="goBack" class="text-sm text-blue-600 hover:underline mb-1">← К реестру</button>
        <h1 class="text-lg font-semibold text-gray-800">РнО-УНП-01. Разрешение на отклонение. Версия 01</h1>
      </div>
      <span :class="statusClass(doc.status)" class="text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
        {{ statusLabel(doc.status) }}
      </span>
    </div>

    <div v-if="loading" class="text-sm text-gray-500">Загрузка...</div>

    <div v-else class="space-y-6">
      <!-- Шапка документа -->
      <section class="bg-white border border-gray-200 rounded-lg p-4 grid grid-cols-3 gap-4">
        <div>
          <label class="block text-sm text-gray-700 mb-1">№ разрешения</label>
          <span v-if="isReadOnly" class="block text-sm text-gray-700 h-[39px] leading-[39px]">{{ doc.number || '—' }}</span>
          <input
            v-else
            v-model="doc.number"
            type="text"
            placeholder="РнО-..."
            class="w-full border border-gray-300 rounded-lg px-3 text-sm h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label class="block text-sm text-gray-700 mb-1">Дата</label>
          <span v-if="isReadOnly" class="block text-sm text-gray-700 h-[39px] leading-[39px]">{{ permitDateDisplay }}</span>
          <input
            v-else
            :value="permitDateDisplay"
            type="text"
            disabled
            class="w-full border border-gray-300 rounded-lg px-3 text-sm bg-gray-100 h-[39px]"
          />
        </div>
        <div>
          <label class="block text-sm text-gray-700 mb-1">Ответственный</label>
          <span v-if="isReadOnly" class="block text-sm text-gray-700 h-[39px] leading-[39px]">{{ creatorDisplay }}</span>
          <input
            v-else
            :value="creatorDisplay"
            type="text"
            disabled
            class="w-full border border-gray-300 rounded-lg px-3 text-sm bg-gray-100 h-[39px]"
          />
        </div>
        <div>
          <label class="block text-sm text-gray-700 mb-1">Источник документа</label>
          <span v-if="isReadOnly" class="block text-sm text-gray-700 h-[39px] leading-[39px]">{{ originDisplay }}</span>
          <input
            v-else
            :value="originDisplay"
            type="text"
            disabled
            class="w-full border border-gray-300 rounded-lg px-3 text-sm bg-gray-100 h-[39px]"
          />
        </div>
      </section>

      <!-- Область разрешения -->
      <section class="bg-white border border-gray-200 rounded-lg p-4">
        <h2 class="font-semibold text-gray-800 mb-3">Область разрешения</h2>
        <table class="w-full text-sm border border-gray-200 table-fixed">
          <colgroup>
            <col style="width: 5%" />
            <col style="width: 47%" />
            <col style="width: 24%" />
            <col style="width: 18%" />
            <col style="width: 6%" />
          </colgroup>
          <thead class="bg-gray-50">
            <tr>
              <th class="px-2 py-1 text-center border-b">№</th>
              <th class="px-2 py-1 text-left border-b">Продукт</th>
              <th class="px-2 py-1 text-left border-b">Модель/вариант исполнения</th>
              <th class="px-2 py-1 text-left border-b">Код проекта</th>
              <th class="px-2 py-1 border-b"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="items.length === 0">
              <td colspan="5" class="px-2 py-2 text-gray-400 text-center">Нет строк</td>
            </tr>
            <template v-for="(item, index) in items" :key="index">
            <tr class="border-t border-gray-200">
              <td class="px-2 py-1 align-top text-center text-gray-500">{{ index + 1 }}</td>
              <td class="px-2 py-1 align-top">
                <span v-if="isReadOnly" class="text-sm text-gray-700">{{ item.product_name || '—' }}</span>
                <div v-else class="flex items-start gap-1">
                  <div class="flex-1 min-w-0">
                    <div v-if="!item.manual" class="v-select-filter" style="width: 100%; flex: none;">
                      <v-select
                        v-model="item.selected"
                        :options="productResults"
                        :filterable="false"
                        :loading="productLoading"
                        :get-option-label="productLabel"
                        label="name"
                        placeholder="Выберите из номенклатуры"
                        :disabled="isReadOnly"
                        @search="(q) => onProductSearch(q, index)"
                        @open="() => onProductSearch('', index)"
                        @update:modelValue="(val) => onProductSelected(index, val)"
                      >
                        <template #option="o">
                          <span>{{ productLabel(o) }}</span>
                        </template>
                        <template #no-options>
                          <span v-if="productLoading">Загрузка...</span>
                          <span v-else>Ничего не найдено</span>
                        </template>
                        <template #list-footer>
                          <li v-if="productHasMore" class="px-3 py-2 text-center border-t">
                            <button
                              type="button"
                              @click.prevent.stop="loadMoreProducts"
                              :disabled="productLoading"
                              class="text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                            >
                              {{ productLoading ? 'Загрузка...' : 'Загрузить ещё...' }}
                            </button>
                          </li>
                        </template>
                      </v-select>
                    </div>
                    <input
                      v-else
                      v-model="item.product_name"
                      :disabled="isReadOnly"
                      type="text"
                      placeholder="Ручной ввод"
                      class="w-full border border-gray-300 rounded-lg px-3 text-sm disabled:bg-gray-100 h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <button
                    v-if="!isReadOnly"
                    type="button"
                    @click="toggleManual(index)"
                    :title="item.manual ? 'Выбрать из номенклатуры' : 'Ввести вручную'"
                    class="flex-shrink-0 h-[39px] w-8 inline-flex items-center justify-center border border-gray-300 rounded text-gray-500 hover:bg-gray-50"
                  >
                    {{ item.manual ? '☰' : '✎' }}
                  </button>
                </div>
              </td>
              <td class="px-2 py-1 align-top">
                <!-- Всегда текстовый инпут: значение автоподставляется из атрибутов продукта,
                     если поле пустое; введённое вручную не перетирается -->
                <span v-if="isReadOnly" class="text-sm text-gray-700">{{ item.model_variant || '—' }}</span>
                <input
                  v-else
                  v-model="item.model_variant"
                  type="text"
                  placeholder="Вариант исполнения"
                  class="w-full border border-gray-300 rounded-lg px-3 text-sm h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <!-- Селект выбора из списка + переключатель режима временно скрыты (оставлены на будущее):
                <div class="flex items-start gap-1">
                  <div class="flex-1 min-w-0">
                    <div v-if="!item.manualVariant" class="v-select-filter" style="width: 100%; flex: none;">
                      <v-select
                        v-model="item.model_variant"
                        :options="variantOptions"
                        :disabled="isReadOnly"
                        placeholder="Выберите из списка"
                      />
                    </div>
                    <input
                      v-else
                      v-model="item.model_variant"
                      :disabled="isReadOnly"
                      type="text"
                      placeholder="Ручной ввод"
                      class="w-full border border-gray-300 rounded-lg px-3 text-sm disabled:bg-gray-100 h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <button
                    v-if="!isReadOnly"
                    type="button"
                    @click="toggleManualVariant(index)"
                    :title="item.manualVariant ? 'Выбрать из списка' : 'Ввести вручную'"
                    class="flex-shrink-0 h-[39px] w-8 inline-flex items-center justify-center border border-gray-300 rounded text-gray-500 hover:bg-gray-50"
                  >
                    {{ item.manualVariant ? '☰' : '✎' }}
                  </button>
                </div>
                -->
              </td>
              <td class="px-2 py-1 align-top">
                <span v-if="isReadOnly" class="text-sm text-gray-700">{{ item.project_code || '—' }}</span>
                <input
                  v-else
                  v-model="item.project_code"
                  type="text"
                  class="w-full border border-gray-300 rounded-lg px-3 text-sm h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </td>
              <td class="px-2 py-1 align-top text-center">
                <button
                  v-if="!isReadOnly"
                  type="button"
                  @click="removeItem(index)"
                  class="h-[39px] text-red-600 hover:text-red-800"
                  title="Удалить строку"
                >
                  ✕
                </button>
              </td>
            </tr>
            <!-- Привязка строки к конкретному изделию (ТЗ-7.3): необязательна -->
            <tr class="bg-gray-50">
              <td class="px-2 pb-2 text-gray-400"></td>
              <td class="px-2 pb-2" colspan="4">
                <div class="text-xs font-semibold text-gray-600 mb-1">Привязка к изделию</div>
                <ProductBindingSelector
                  v-model="item.binding"
                  :read-only="isReadOnly"
                  @product-picked="(picked) => onBindingProductPicked(index, picked)"
                />
              </td>
            </tr>
            </template>
          </tbody>
        </table>
        <div v-if="!isReadOnly" class="mt-3">
          <button type="button" @click="addItem" class="text-sm text-blue-600 hover:underline">+ Добавить строку</button>
        </div>
      </section>

      <!-- Причина инициирования -->
      <section class="bg-white border border-gray-200 rounded-lg p-4">
        <label class="block text-sm font-semibold text-gray-800 mb-1">Причина инициирования</label>
        <span v-if="isReadOnly" class="block text-sm text-gray-700 whitespace-pre-line">{{ doc.reason || '—' }}</span>
        <textarea
          v-else
          v-model="doc.reason"
          rows="2"
          class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        ></textarea>
      </section>

      <!-- Действия по отклонению -->
      <section class="bg-white border border-gray-200 rounded-lg p-4">
        <label class="block text-sm font-semibold text-gray-800 mb-1">Действия по отклонению</label>
        <div class="flex flex-wrap items-center gap-2 mb-2 text-sm text-gray-700">
          <span>По выявленному отклонению была проведена оценка рисков №</span>
          <span v-if="isReadOnly" class="font-medium">{{ doc.risk_assessment_number || '—' }}</span>
          <div v-else class="flex items-start gap-1">
            <div v-if="!riskManual" class="v-select-filter" style="width: 220px; flex: none;">
              <v-select
                v-model="doc.risk_assessment_number"
                :options="riskOptions"
                placeholder="Выберите ОР"
              >
                <template #no-options>Нет сохранённых оценок рисков с номером</template>
              </v-select>
            </div>
            <input
              v-else
              v-model="doc.risk_assessment_number"
              type="text"
              placeholder="ОР-00xx"
              class="w-[220px] border border-gray-300 rounded-lg px-3 text-sm h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              type="button"
              @click="toggleRiskManual"
              :title="riskManual ? 'Выбрать из списка' : 'Ввести вручную'"
              class="flex-shrink-0 h-[39px] w-8 inline-flex items-center justify-center border border-gray-300 rounded text-gray-500 hover:bg-gray-50"
            >
              {{ riskManual ? '☰' : '✎' }}
            </button>
          </div>
        </div>
        <span v-if="isReadOnly" class="block text-sm text-gray-700 whitespace-pre-line">{{ doc.actions || '—' }}</span>
        <textarea
          v-else
          v-model="doc.actions"
          rows="2"
          placeholder="Действия по отклонению"
          class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        ></textarea>
      </section>

      <!-- Вывод -->
      <section class="bg-white border border-gray-200 rounded-lg p-4">
        <label class="block text-sm font-semibold text-gray-800 mb-1">Вывод</label>
        <span v-if="isReadOnly" class="block text-sm text-gray-700 whitespace-pre-line">{{ doc.conclusion || '—' }}</span>
        <textarea
          v-else
          v-model="doc.conclusion"
          rows="2"
          class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        ></textarea>
      </section>

      <!-- Приложения -->
      <section class="bg-white border border-gray-200 rounded-lg p-4">
        <label class="block text-sm font-semibold text-gray-800 mb-1">Приложения</label>
        <span v-if="isReadOnly" class="block text-sm text-gray-700 whitespace-pre-line">{{ doc.attachments_note || '—' }}</span>
        <textarea
          v-else
          v-model="doc.attachments_note"
          rows="2"
          class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        ></textarea>
      </section>

      <!-- Утверждение -->
      <section class="bg-white border border-gray-200 rounded-lg p-4">
        <h2 class="font-semibold text-gray-800 mb-3">Утверждение</h2>
        <table class="w-full text-sm border border-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-2 py-1 text-left border-b">ФИО</th>
              <th class="px-2 py-1 text-left border-b w-1/3">Позиция</th>
              <th class="px-2 py-1 text-left border-b w-44">Дата одобрения</th>
              <th class="px-2 py-1 border-b w-10"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="approvers.length === 0">
              <td colspan="4" class="px-2 py-2 text-gray-400 text-center">Нет согласующих</td>
            </tr>
            <tr v-for="(approver, index) in approvers" :key="index" class="border-t border-gray-200">
              <td class="px-2 py-1 align-middle">
                <div v-if="!isReadOnly" class="v-select-filter" style="width: 100%; flex: none;">
                  <v-select
                    v-model="approver.user_id"
                    :options="users"
                    :reduce="u => u.id"
                    :get-option-label="userName"
                    placeholder="Выберите пользователя"
                  />
                </div>
                <span v-else>{{ approverName(approver) }}</span>
              </td>
              <td class="px-2 py-1 align-middle">
                <input
                  v-if="!isReadOnly"
                  v-model="approver.position_text"
                  type="text"
                  class="w-full border border-gray-300 rounded-lg px-3 text-sm h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <span v-else>{{ approver.position_text || '—' }}</span>
              </td>
              <td class="px-2 py-1 align-middle">
                <span
                  v-if="approver.approved_at"
                  class="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700"
                >
                  Одобрено {{ formatDate(approver.approved_at) }}
                </span>
                <span v-else class="text-gray-400">—</span>
              </td>
              <td class="px-2 py-1 align-middle text-center">
                <button
                  v-if="!isReadOnly"
                  type="button"
                  @click="removeApprover(index)"
                  class="text-red-600 hover:text-red-800"
                  title="Удалить строку"
                >
                  ✕
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="!isReadOnly" class="mt-3">
          <button type="button" @click="addApprover" class="text-sm text-blue-600 hover:underline">+ Добавить согласующего</button>
        </div>
      </section>

      <!-- Кнопки действий -->
      <div class="flex flex-wrap gap-2 justify-end">
        <button
          v-if="canSaveDraft"
          v-can-write
          type="button"
          @click="saveDraft"
          :disabled="saving"
          class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium disabled:opacity-50"
        >
          {{ saving ? 'Сохранение...' : (doc.status === 'draft' ? 'Сохранить как черновик' : 'Сохранить') }}
        </button>
        <button
          v-if="canSendForApproval"
          v-can-write
          type="button"
          @click="sendForApproval"
          :disabled="saving"
          class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded font-medium disabled:opacity-50"
        >
          Отправить на согласование
        </button>
        <button
          v-if="canApprove"
          v-can-write
          type="button"
          @click="approveDoc"
          :disabled="saving"
          class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-medium disabled:opacity-50"
        >
          Одобрить
        </button>
        <button
          v-if="canCancel"
          v-can-write
          type="button"
          @click="confirmCancelShow = true"
          :disabled="saving"
          class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium disabled:opacity-50"
        >
          Отменить
        </button>
      </div>
    </div>

    <!-- Модалка подтверждения отмены -->
    <div v-if="confirmCancelShow" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" @click.self="confirmCancelShow = false">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
        <h3 class="text-lg font-semibold mb-3">Отмена документа</h3>
        <p class="text-sm text-gray-700 mb-5">Отменить документ? После отмены форма станет доступна только для чтения.</p>
        <div class="flex justify-end gap-2">
          <button type="button" class="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300" @click="confirmCancelShow = false">Нет</button>
          <button type="button" class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium" @click="confirmCancelShow = false; cancelDoc()">Отменить документ</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useAuthStore } from '@/stores/auth.js';
import ProductBindingSelector from '@/components/smkDesk/ProductBindingSelector.vue';
import { emptyBinding, bindingFromRow, bindingToPayload } from '@/components/smkDesk/productBinding.js';

export default {
  components: { ProductBindingSelector },
  props: {
    moduleId: { type: [String, Number], required: true },
    permitId: { type: [String, Number], default: null },
    mode: { type: String, default: 'create' },
  },
  data() {
    return {
      docId: this.permitId ? Number(this.permitId) : null,
      localMode: this.mode,
      loading: false,
      saving: false,
      currentUser: null,
      creator: null,
      users: [],
      variantOptions: [],
      confirmCancelShow: false,
      riskManual: false,
      riskOptions: [],
      doc: this.emptyDoc(),
      items: [],
      approvers: [],
      productResults: [],
      productLoading: false,
      productHasMore: false,
      productPage: 1,
      productQuery: '',
    };
  },
  computed: {
    isReadOnly() {
      // approved и cancelled — форма только для чтения (правка запрещена)
      return ['approved', 'cancelled'].includes(this.doc.status);
    },
    permitDateDisplay() {
      // На странице создания (permit_date ещё нет) показываем сегодняшнюю дату
      return this.doc.permit_date ? this.formatDate(this.doc.permit_date) : this.formatDate(new Date());
    },
    creatorDisplay() {
      return this.userName(this.creator);
    },
    // Из какого события создан документ — подпись приходит с бэкенда, ключ на экран не выводим
    originDisplay() {
      return this.doc.origin_label || 'Заведён вручную';
    },
    isCreator() {
      return this.currentUser && this.creator && this.currentUser.id === this.creator.id;
    },
    myApprover() {
      if (!this.currentUser) return null;
      return this.approvers.find(a => a.user_id === this.currentUser.id) || null;
    },
    canSaveDraft() {
      // Сохранять можно во всех статусах кроме согласованного/отменённого
      return !this.isReadOnly;
    },
    canSendForApproval() {
      return this.docId && this.doc.status === 'draft' && this.isCreator;
    },
    canApprove() {
      // Только если строка согласующего сохранена в БД (есть id) — иначе approve по несохранённой строке вернёт 403
      return this.doc.status === 'in_approval' && this.myApprover && this.myApprover.id && !this.myApprover.approved_at;
    },
    canCancel() {
      // Создатель может отменить из любого статуса, кроме уже отменённого
      return this.docId && this.doc.status !== 'cancelled' && this.isCreator;
    },
  },
  mounted() {
    this.currentUser = useAuthStore().currentUser;
    this.loadUsers();
    this.loadVariants();
    this.loadRiskOptions();
    if (this.localMode === 'edit' && this.docId) {
      this.loadDocument();
    } else {
      this.initCreate();
    }
  },
  methods: {
    emptyDoc() {
      return {
        number: 'РнО-',
        permit_date: null,
        reason: '',
        actions: '',
        risk_assessment_number: '',
        conclusion: '',
        attachments_note: '',
        status: 'draft',
        origin_label: '',
      };
    },
    initCreate() {
      this.creator = this.currentUser;
      this.doc = this.emptyDoc();
      this.items = [];
      this.approvers = [];
      this.ensureFirstItem();
      this.ensureFirstApprover();
    },
    async loadUsers() {
      try {
        // Только пользователи с доступом к столу СМК — они могут согласовывать
        const { data } = await axios.get('/api/smk/deviation-permit/users');
        if (data?.success) {
          this.users = data.data || [];
        }
      } catch (e) {
        // Список пользователей не критичен для чтения формы
      }
    },
    async loadVariants() {
      try {
        const { data } = await axios.get('/api/smk/deviation-permit/variants');
        if (data?.success) {
          this.variantOptions = data.data || [];
        }
      } catch (e) {
        // Список вариантов не критичен — поле остаётся с ручным вводом
      }
    },
    async loadRiskOptions() {
      try {
        // Номера оценок рисков из реестра ОР (AND-185) для выбора в поле «номер оценки рисков»
        const { data } = await axios.get('/api/smk/risk-assessment/options');
        if (data?.success) {
          this.riskOptions = (data.data || []).map(o => o.number).filter(Boolean);
        }
      } catch (e) {
        console.error('Не удалось загрузить список оценок рисков', e);
      }
    },
    toggleRiskManual() {
      // Переключение между выбором из реестра ОР и ручным вводом — значение сохраняется
      this.riskManual = !this.riskManual;
    },
    async loadDocument() {
      this.loading = true;
      try {
        const { data } = await axios.get(`/api/smk/deviation-permit/${this.docId}`);
        if (data?.success) {
          this.applyDocument(data.data);
          this.ensureFirstItem();
          this.ensureFirstApprover();
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка загрузки документа');
      } finally {
        this.loading = false;
      }
    },
    applyDocument(d) {
      this.docId = d.id;
      this.localMode = 'edit';
      this.creator = d.creator || null;
      this.doc = {
        number: d.number || '',
        permit_date: d.permit_date || null,
        reason: d.reason || '',
        actions: d.actions || '',
        risk_assessment_number: d.risk_assessment_number || '',
        conclusion: d.conclusion || '',
        attachments_note: d.attachments_note || '',
        status: d.status,
        origin_label: d.origin_label || '',
      };
      this.items = (d.items || []).map(i => ({
        id: i.id,
        product_id: i.product_id,
        product_name: i.product_name || '',
        model_variant: i.model_variant || '',
        project_code: i.project_code || '',
        selected: i.product_id ? { id: i.product_id, name: i.product_name } : null,
        // Ручной ввод продукта, если есть название без привязки к номенклатуре
        manual: !!(i.product_name && !i.product_id),
        manualVariant: false,
        binding: bindingFromRow(i),
      }));
      this.approvers = (d.approvers || []).map(a => ({
        id: a.id,
        user_id: a.user_id,
        position_text: a.position_text || '',
        approved_at: a.approved_at || null,
      }));
    },
    buildPayload() {
      return {
        number: this.doc.number || null,
        reason: this.doc.reason || null,
        actions: this.doc.actions || null,
        risk_assessment_number: this.doc.risk_assessment_number || null,
        conclusion: this.doc.conclusion || null,
        attachments_note: this.doc.attachments_note || null,
        items: this.items.map((i, idx) => ({
          id: i.id || undefined,
          product_id: i.product_id || null,
          product_name: i.product_name || null,
          model_variant: i.model_variant || null,
          project_code: i.project_code || null,
          sort: idx,
          ...bindingToPayload(i.binding),
        })),
        approvers: this.approvers
          .filter(a => a.user_id)
          .map((a, idx) => ({
            id: a.id || undefined,
            user_id: a.user_id,
            position_text: a.position_text || null,
            sort: idx,
          })),
      };
    },
    async persist() {
      const payload = this.buildPayload();
      if (!this.docId) {
        const { data } = await axios.post('/api/smk/deviation-permit', payload);
        if (data?.success) {
          this.applyDocument(data.data);
          // Сохраняем query (фильтры реестра), чтобы при возврате реестр восстановился
          this.$router.replace({
            path: `/smk-desk/${this.moduleId}/deviation-permit/${this.docId}`,
            query: this.$route.query,
          });
          return true;
        }
        return false;
      }
      const { data } = await axios.put(`/api/smk/deviation-permit/${this.docId}`, payload);
      if (data?.success) {
        this.applyDocument(data.data);
        return true;
      }
      return false;
    },
    async saveDraft() {
      this.saving = true;
      try {
        if (await this.persist()) {
          this.notifySuccess('Документ сохранён');
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка сохранения');
      } finally {
        this.saving = false;
      }
    },
    async sendForApproval() {
      this.saving = true;
      try {
        if (!(await this.persist())) return;
        const { data } = await axios.post(`/api/smk/deviation-permit/${this.docId}/send-for-approval`);
        if (data?.success) {
          this.applyDocument(data.data);
          this.notifySuccess('Документ отправлен на согласование');
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка отправки на согласование');
      } finally {
        this.saving = false;
      }
    },
    async approveDoc() {
      this.saving = true;
      try {
        const { data } = await axios.post(`/api/smk/deviation-permit/${this.docId}/approve`);
        if (data?.success) {
          this.applyDocument(data.data);
          this.notifySuccess('Документ одобрен');
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка одобрения');
      } finally {
        this.saving = false;
      }
    },
    async cancelDoc() {
      this.saving = true;
      try {
        const { data } = await axios.post(`/api/smk/deviation-permit/${this.docId}/cancel`);
        if (data?.success) {
          this.applyDocument(data.data);
          this.notifySuccess('Документ отменён');
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка отмены');
      } finally {
        this.saving = false;
      }
    },
    addItem() {
      this.items.push({ id: null, product_id: null, product_name: '', model_variant: '', project_code: '', selected: null, manual: false, manualVariant: false, binding: emptyBinding() });
    },
    // Изделие выбрано — подставляем номенклатуру в пустые поля строки, заполненное не трогаем
    onBindingProductPicked(index, picked) {
      const item = this.items[index];
      if (!item || !picked) return;
      if (picked.product_id && !item.product_id && !item.manual) {
        item.product_id = picked.product_id;
        item.product_name = picked.product_name || item.product_name;
        item.selected = { id: picked.product_id, name: picked.product_name };
      }
    },
    ensureFirstItem() {
      // Если таблица пустая и форма редактируема — добавляем первую строку автоматически
      if (!this.isReadOnly && this.items.length === 0) {
        this.addItem();
      }
    },
    toggleManual(index) {
      const item = this.items[index];
      item.manual = !item.manual;
      // Сбрасываем привязку к номенклатуре при любом переключении режима
      item.selected = null;
      item.product_id = null;
      if (!item.manual) {
        // Переход к выбору из номенклатуры — очищаем ручной текст
        item.product_name = '';
      }
    },
    toggleManualVariant(index) {
      // Переключение между выбором из списка и ручным вводом — значение поля сохраняется
      this.items[index].manualVariant = !this.items[index].manualVariant;
    },
    removeItem(index) {
      this.items.splice(index, 1);
    },
    addApprover() {
      this.approvers.push({ id: null, user_id: null, position_text: '', approved_at: null });
    },
    ensureFirstApprover() {
      // Если таблица согласующих пустая и форма редактируема — добавляем первую строку
      if (!this.isReadOnly && this.approvers.length === 0) {
        this.addApprover();
      }
    },
    removeApprover(index) {
      this.approvers.splice(index, 1);
    },
    productLabel(option) {
      if (!option) return '';
      return option.sku ? `${option.sku} — ${option.name}` : option.name;
    },
    onProductSearch(query, index) {
      // index не используется: открыт всегда один выпадающий список, результат общий
      this.productQuery = query || '';
      this.productPage = 1;
      clearTimeout(this._productTimer);
      this._productTimer = setTimeout(() => this.fetchProducts(false), 300);
    },
    async fetchProducts(append) {
      this.productLoading = true;
      try {
        const { data } = await axios.post('/api/product/getProducts', {
          search: this.productQuery.trim(),
          page: this.productPage,
          per_page: 20,
        });
        const paginator = data?.data || {};
        const rows = paginator.data || [];
        this.productResults = append ? [...this.productResults, ...rows] : rows;
        const current = paginator.current_page || this.productPage;
        const last = paginator.last_page || current;
        this.productHasMore = current < last;
      } catch (e) {
        if (!append) this.productResults = [];
        this.productHasMore = false;
      } finally {
        this.productLoading = false;
      }
    },
    loadMoreProducts() {
      if (this.productLoading || !this.productHasMore) return;
      this.productPage += 1;
      this.fetchProducts(true);
    },
    onProductSelected(index, val) {
      // val — выбранная позиция номенклатуры
      this.items[index].selected = val || null;
      this.items[index].product_id = val && val.id ? val.id : null;
      this.items[index].product_name = val ? val.name : '';
      // Автоподстановка варианта исполнения из атрибутов продукта (только если поле пустое)
      if (val && val.id) {
        this.fillVariantFromProduct(index, val.id);
      }
    },
    async fillVariantFromProduct(index, productId) {
      // Не перетираем уже введённое/подставленное значение
      if (this.items[index].model_variant) return;
      try {
        const { data } = await axios.post('/api/product/getProduct', { id: productId });
        const product = data?.product || {};
        const values = product.attribute_values || product.attributeValues || [];
        // Приоритет источника: «Модуль/Модель» → «Вариант исполнения» → ничего
        const priorityGroups = [['модул', 'модель'], ['вариант']];
        let picked = '';
        for (const group of priorityGroups) {
          const found = values.find(v => {
            const name = (v.attribute?.name || '').toLowerCase();
            return v.value && group.some(p => name.includes(p));
          });
          if (found) {
            picked = found.value;
            break;
          }
        }
        if (picked && !this.items[index].model_variant) {
          this.items[index].model_variant = picked;
        }
      } catch (e) {
        // Автоподстановка не критична — поле остаётся для ручного ввода
      }
    },
    goBack() {
      // Возврат к реестру РнО: открываем вкладку «Разрешение на отклонение»
      // и восстанавливаем фильтры/страницу (из query формы, переданного реестром)
      this.$router.push({
        path: `/smk-desk/${this.moduleId}`,
        query: { ...this.$route.query, tab: 'deviation' },
      });
    },
    userName(user) {
      if (!user) return '—';
      const full = [user.last_name, user.first_name, user.patronymic].filter(Boolean).join(' ');
      return full || user.name || user.email || ('ID ' + user.id);
    },
    approverName(approver) {
      if (approver.user) return this.userName(approver.user);
      const u = this.users.find(x => x.id === approver.user_id);
      if (u) return this.userName(u);
      if (this.creator && this.creator.id === approver.user_id) return this.userName(this.creator);
      return approver.user_id ? ('ID ' + approver.user_id) : '—';
    },
    statusLabel(status) {
      return {
        draft: 'Новый',
        in_approval: 'В работе',
        approved: 'Согласован',
        cancelled: 'Отменён',
      }[status] || status;
    },
    statusClass(status) {
      return {
        draft: 'bg-gray-100 text-gray-700',
        in_approval: 'bg-yellow-100 text-yellow-700',
        approved: 'bg-green-100 text-green-700',
        cancelled: 'bg-red-100 text-red-700',
      }[status] || 'bg-gray-100 text-gray-700';
    },
    formatDate(value) {
      if (!value) return '—';
      const d = new Date(value);
      if (isNaN(d.getTime())) return '—';
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
    },
    notifySuccess(message) {
      if (this.$root?.$toast?.success) {
        this.$root.$toast.success(message);
      }
    },
    notifyError(error, fallback) {
      const msg = error?.response?.data?.message || fallback || 'Ошибка';
      if (this.$root?.$toast?.error) {
        this.$root.$toast.error(msg);
      } else {
        alert(msg);
      }
    },
  },
};
</script>
